// Reglas de presentación y de conflicto de la lista de agentes IA, fuera de la
// vista para poder testearlas sin montar React.
//
// Modelo mental del alcance de un agente: `scope.channel_toggles` dice en qué
// canales responde (una clave ausente = responde, igual que en aiScope.js) y
// `scope.account_overrides` marca las excepciones por cuenta. Acá se traduce eso
// a las mismas estructuras que ya usa el formulario base, para reutilizar
// `disabledScopeLabels` en vez de escribir una segunda versión del resumen.

import { disabledScopeLabels } from "./aiScope";

// Estados del agente (contrato): active | paused | draft.
export const AGENT_STATUS_ACTIVE = "active";

export const AGENT_STATUS_LABELS = {
  active: "Activo",
  paused: "Pausado",
  draft: "Borrador",
};

// Tono del chip por estado: verde solo para el que responde; el borrador es un
// estado legítimo (nunca se activó), no una alerta.
export const AGENT_STATUS_TONES = {
  active: "success",
  paused: "muted",
  draft: "neutral",
};

// Audiencias del contrato. El catálogo del backend manda; esta copia evita
// mostrar el id crudo cuando el catálogo no trae la lista (backend viejo).
export const AUDIENCE_FALLBACK_LABELS = {
  customer: "Clientes",
  merchant: "Comercios",
  driver: "Repartidores",
  internal: "Equipo interno",
};

// `false` explícito es lo único que apaga: ausente hereda (encendido).
const isOn = (value) => value !== false;

/**
 * Etiqueta de la audiencia: la del catálogo si existe, la copia local si no, y
 * el id crudo como último recurso (mejor un id que un hueco).
 * @param {{audiences?: Array}|null} catalog
 * @param {string} audienceId
 * @returns {string}
 */
export function audienceLabel(catalog, audienceId) {
  if (!audienceId) return "";
  const fromCatalog = (catalog?.audiences || []).find((a) => a?.id === audienceId);
  if (fromCatalog?.label) return fromCatalog.label;
  return AUDIENCE_FALLBACK_LABELS[audienceId] || audienceId;
}

/**
 * Proveedor/modelo del agente resuelto contra el catálogo. `inherit: true`
 * significa "usa lo que esté configurado en la configuración base del courier".
 * @param {object} agent
 * @param {{providers?: Array}|null} catalog
 * @returns {{inherit: boolean, label?: string, model?: string}}
 */
export function providerSummary(agent, catalog) {
  const provider = agent?.provider || {};
  // Sin `inherit_tenant` explícitamente en false, un agente sin proveedor
  // propio se lee como heredado: es lo que el backend hace por defecto.
  if (!provider.kind || provider.inherit_tenant === true) return { inherit: true };
  const known = (catalog?.providers || []).find((p) => p?.id === provider.kind);
  return {
    inherit: false,
    label: known?.label || provider.kind,
    model: provider.model || "",
  };
}

/**
 * Estado efectivo del alcance de un agente traducido a las estructuras de
 * `disabledScopeLabels` (channelStates / accountStates), tomando los canales y
 * las cuentas del GET de alcance del tenant (la fuente de las claves y los
 * labels; el front nunca los inventa).
 * @param {object} agent
 * @param {Array} scope - canales del tenant (getAiChannelScope)
 * @returns {{channelStates: object, accountStates: object}}
 */
export function agentScopeStates(agent, scope) {
  const toggles = agent?.scope?.channel_toggles || {};
  const overrides = new Map(
    (Array.isArray(agent?.scope?.account_overrides) ? agent.scope.account_overrides : [])
      .filter((o) => o && typeof o.key === "string")
      .map((o) => [o.key, o.enabled !== false]),
  );

  const channelStates = {};
  const accountStates = {};
  (Array.isArray(scope) ? scope : []).forEach((channel) => {
    const channelOn = isOn(toggles[channel.channel]);
    channelStates[channel.channel] = channelOn;
    (channel.accounts || []).forEach((account) => {
      accountStates[account.key] = overrides.has(account.key)
        ? overrides.get(account.key)
        : channelOn;
    });
  });
  return { channelStates, accountStates };
}

/**
 * Dónde NO responde este agente, en palabras (mismo texto que la configuración
 * base). Devuelve [] cuando responde en todos los canales del tenant.
 *
 * Los canales que el agente apaga y que el alcance del tenant no lista se
 * nombran por su id: sin eso, un alcance que no cargó se leería como "responde
 * en todos los canales" cuando el agente tiene canales apagados.
 * @param {object} agent
 * @param {Array} scope
 * @returns {string[]}
 */
export function agentDisabledScopeLabels(agent, scope) {
  const { channelStates, accountStates } = agentScopeStates(agent, scope);
  const labels = disabledScopeLabels(scope, channelStates, accountStates);
  const known = new Set((Array.isArray(scope) ? scope : []).map((c) => c.channel));
  const extra = Object.entries(agent?.scope?.channel_toggles || {})
    .filter(([channel, on]) => on === false && !known.has(channel))
    .map(([channel]) => channel);
  return [...labels, ...extra];
}

/** Claves de cuenta con excepción explícita en el alcance del agente. */
function overrideKeys(agent) {
  const list = agent?.scope?.account_overrides;
  return (Array.isArray(list) ? list : [])
    .filter((o) => o && typeof o.key === "string")
    .map((o) => o.key);
}

/** Estado de una cuenta para el agente: sin excepción explícita, responde. */
function overrideOn(agent, key) {
  const entry = (agent?.scope?.account_overrides || []).find((o) => o?.key === key);
  return entry ? entry.enabled !== false : true;
}

/**
 * Firma de las excepciones del agente. Dos agentes con la MISMA firma cubren el
 * mismo terreno a nivel canal (mismo encendido/apagado por cuenta); con firmas
 * distintas uno de los dos se está quedando con una parte, y ese reparto es
 * justamente lo que evita el choque.
 */
function overrideSignature(agent) {
  const list = Array.isArray(agent?.scope?.account_overrides)
    ? agent.scope.account_overrides
    : [];
  return list
    .filter((o) => o && typeof o.key === "string")
    .map((o) => `${o.key}:${o.enabled !== false}`)
    .sort()
    .join("|");
}

/**
 * Pares de agentes ACTIVOS de la misma audiencia cuyo alcance se solapa: ambos
 * responderían a la vez en el mismo canal (o en la misma cuenta). No es un
 * error del backend —dos agentes pueden repartirse canales a propósito—, por
 * eso se avisa en vez de bloquear.
 *
 * Criterios:
 *  - mismo canal: encendido en ambos, con las MISMAS excepciones de cuenta (si
 *    cada uno se queda con números distintos, el canal está repartido y no hay
 *    choque);
 *  - sin canales declarados por ninguno (`allChannels`), ambos responden en
 *    todos los canales del courier;
 *  - misma cuenta: la cuenta no está apagada en ninguno de los dos.
 * Un canal apagado en cualquiera de los dos, o una cuenta apagada en cualquiera,
 * descarta ese solapamiento.
 *
 * @param {Array} agents
 * @returns {Array<{aId, bId, aName, bName, audience, channels: string[],
 *   accounts: string[], allChannels: boolean}>}
 */
export function detectScopeConflicts(agents) {
  const list = (Array.isArray(agents) ? agents : []).filter(
    (a) => a && a.status === AGENT_STATUS_ACTIVE,
  );
  const conflicts = [];

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i];
      const b = list[j];
      if (a.audience !== b.audience) continue;

      // Mismas excepciones de cuenta en ambos => cubren el mismo terreno a
      // nivel canal. Con excepciones distintas uno se está quedando con una
      // parte, y ahí el canal compartido no implica choque.
      const sameOverrides = overrideSignature(a) === overrideSignature(b);

      const togglesA = a?.scope?.channel_toggles || {};
      const togglesB = b?.scope?.channel_toggles || {};
      const namedChannels = Array.from(
        new Set([...Object.keys(togglesA), ...Object.keys(togglesB)]),
      );
      const sharedChannels = sameOverrides
        ? namedChannels.filter((channel) => isOn(togglesA[channel]) && isOn(togglesB[channel]))
        : [];

      const namedAccounts = Array.from(
        new Set([...overrideKeys(a), ...overrideKeys(b)]),
      );
      const sharedAccounts = namedAccounts.filter(
        (key) => overrideOn(a, key) && overrideOn(b, key),
      );

      // Ninguno declaró canales y ninguno se reservó cuentas: los dos responden
      // en todos. Es el solapamiento más amplio y el que más conviene avisar.
      const allChannels = namedChannels.length === 0 && sameOverrides;

      if (!allChannels && sharedChannels.length === 0 && sharedAccounts.length === 0) {
        continue;
      }

      conflicts.push({
        aId: a.id,
        bId: b.id,
        aName: a.name,
        bName: b.name,
        audience: a.audience,
        channels: sharedChannels,
        accounts: sharedAccounts,
        allChannels,
      });
    }
  }

  return conflicts;
}
